import React from 'react';

import { Guid } from 'src/application/store/model/guid';

import { useContentWithDelay } from '../hooks/useContentWithDelay';
import { AudioWidget } from './AudioWidget/AudioWidget';
import { DividerWidget } from './DividerWidget/DividerWidget';
import { FillInQuestionWidget } from './FillInQuestionWidget/FillInQuestionWidget';
import { FlipCardWidget } from './FlipCardWidget/FlipCardWidget';
import { HiddenAreaWidget } from './HiddenAreaWidget/HiddenAreaWidget';
import { HTMLWidget } from './HTMLWidget/HTMLWidget';
import { ImageCarouselWidgetComponent } from './ImageCarouselWidget/ImageCarouselWidgetComponent';
import { ImageWidget } from './ImageWidget/ImageWidget';
import { InteractiveProcessWidgetComponent } from './InteractiveProcessWidget/InteractiveProcessWidgetComponent';
import { InteractiveTabsWidgetComponent } from './InteractiveTabsWidget/InteractiveTabsWidgetComponent';
import { LabeledGraphicWidget } from './LabeledGraphicWidget/LabeledGraphicWidget';
import { ListWidget } from './ListWidget/ListWidget';
import { MultipleResponseQuestionWidget } from './MultipleResponseQuestionWidget/MultipleResponseQuestionWidget';
import { PDFWidget } from './PDFWidget/PDFWidget';
import { QuestionWidget } from './QuestionWidget/QuestionWidget';
import { SortationWidget } from './SortationWIdget/SortationWidget';
import { TextOnImageWidget } from './TextOnImageWidget/TextOnImageWidget';
import { TextWidget } from './TextWidget/TextWidget';
import { VideoWidget } from './VideoWidget/VideoWidget';
import { Widget as WidgetModel, WidgetType } from '@ael/core-api';
import classNames from 'classnames';

import './Widget.scss';
import { FroalaEditorsSettings } from 'src/config/configs';

export interface StudentWidgetProps {
  contentItemId: Guid;
  widget?: WidgetModel;
  sectionDisplayDelayInSeconds?: number;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export interface ExistingStudentWidgetProps {
  widget: WidgetModel;
  isVisible: boolean;
  froalaEditorsSettings: FroalaEditorsSettings;
}

const WidgetTypeToComponent: Record<string, React.ComponentType<ExistingStudentWidgetProps>> = {
  [WidgetType.text]: TextWidget,
  [WidgetType.image]: ImageWidget,
  [WidgetType.pdf]: PDFWidget,
  [WidgetType.video]: VideoWidget,
  [WidgetType.question]: QuestionWidget,
  [WidgetType.fillInQuestion]: FillInQuestionWidget,
  [WidgetType.multipleResponseQuestion]: MultipleResponseQuestionWidget,
  [WidgetType.html]: HTMLWidget,
  [WidgetType.hiddenArea]: HiddenAreaWidget,
  [WidgetType.sortation]: SortationWidget,
  [WidgetType.audio]: AudioWidget,
  [WidgetType.interactiveProcess]: InteractiveProcessWidgetComponent,
  [WidgetType.imageCarousel]: ImageCarouselWidgetComponent,
  [WidgetType.interactiveTabs]: InteractiveTabsWidgetComponent,
  [WidgetType.flipCard]: FlipCardWidget,
  [WidgetType.textOnImage]: TextOnImageWidget,
  [WidgetType.list]: ListWidget,
  [WidgetType.labeledGraphic]: LabeledGraphicWidget,
  [WidgetType.divider]: DividerWidget,
};

export const Widget: React.FC<StudentWidgetProps> = ({ contentItemId, widget, sectionDisplayDelayInSeconds, froalaEditorsSettings }) => {
  const { isVisible, ref } = useContentWithDelay(
    contentItemId,
    widget?.value.properties.displayDelayInSeconds || 0,
    sectionDisplayDelayInSeconds
  );

  if (widget !== undefined) {
    const WidgetComponent = WidgetTypeToComponent[widget.value.type];
    return (
      <div
        className={classNames('fade-enter', { 'fade-enter-active': isVisible }, { 'widget--disabled': !isVisible })}
        ref={ref}
      >
        <WidgetComponent widget={widget} froalaEditorsSettings={froalaEditorsSettings} isVisible={isVisible} />
      </div>
    );
  }

  return null;
};
